import { useState, useEffect } from "react";
import { Button, Card, Container, Col, Row } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { gettravelHotel } from "../../store/slices/travelhotel";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getPlace } from "../../store/slices/place";

export default function RoomType() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { travelHotelList = [] } = useSelector(
    (state) => state.travelHotel
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const pageLimit = 8;

  const [searchName, setSearchName] = useState("");
  const [category, setCategory] = useState("");
  const [location, setLocation] = useState("");
  const [places, setPlaces] = useState([]);

  /* =========================
     GET PLACES
  ========================= */
  useEffect(() => {
    const fetchPlaces = async () => {
      try {
        const res = await dispatch(getPlace()).unwrap();
        setPlaces(res?.places || []);
      } catch (error) {
        console.error("Failed to fetch places:", error);
      }
    };

    fetchPlaces();
  }, [dispatch]);

  /* =========================
     GET HOTELS
  ========================= */
  useEffect(() => {
    const fetchHotels = async () => {
      try {
        setLoading(true);

        const params = {};

        if (searchName.trim()) {
          params.name = searchName.trim();
        }

        if (category) {
          params.category = category;
        }

        if (location) {
          params.location = location;
        }

        await dispatch(gettravelHotel(params)).unwrap();

        // Always start from page 1 when filters change
        setCurrentPage(1);
      } catch (error) {
        console.error("Failed to fetch hotels:", error);
        toast.error("Failed to fetch hotels");
      } finally {
        setLoading(false);
      }
    };

    fetchHotels();
  }, [dispatch, searchName, category, location]);

  /* =========================
     HOTEL LIST
  ========================= */
  const hotelList = Array.isArray(travelHotelList)
    ? travelHotelList
    : [];

  const totalPages = Math.ceil(hotelList.length / pageLimit);

  /*
    If filtering reduces the number of pages,
    make sure current page remains valid.
  */
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }

    if (totalPages === 0 && currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  const startIndex = (currentPage - 1) * pageLimit;

  const currentHotels = hotelList.slice(
    startIndex,
    startIndex + pageLimit
  );

  /* =========================
     RESET FILTERS
  ========================= */
  const handleReset = () => {
    setSearchName("");
    setCategory("");
    setLocation("");
    setCurrentPage(1);
  };

  /* =========================
     PAGE CHANGE
  ========================= */
  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;

    setCurrentPage(page);
  };

  /* =========================
     PAGINATION
  ========================= */
  const paginationPages = Array.from(
    { length: totalPages },
    (_, i) => i + 1
  ).filter(
    (page) =>
      page === 1 ||
      page === totalPages ||
      Math.abs(page - currentPage) <= 1
  );

  return (
    <>
      {/* =========================
          BREADCRUMB
      ========================= */}
      <div className="breadcrumb-bar mb-2">
        <div className="breadcrumb-left">
          <Link
            to="/admin/home"
            className="breadcrumb-link active"
          >
            Home
          </Link>

          <span className="breadcrumb-separator">|</span>

          <span
            className="breadcrumb-link inactive"
            onClick={() => navigate("/admin/roomtype")}
          >
            Room Type
          </span>
        </div>
      </div>

      {/* =========================
          FILTERS
      ========================= */}
      <Row className="mb-3 p-3">
        <Col md={3}>
          <input
            type="text"
            className="form-control"
            placeholder="Search hotel name"
            value={searchName}
            onChange={(e) => {
              setSearchName(e.target.value);
              setCurrentPage(1);
            }}
          />
        </Col>

        <Col md={3}>
          <select
            className="form-select"
            value={category}
            onChange={(e) => {
              setCategory(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All Category</option>
            <option value="Budget">Budget</option>
            <option value="2 Star">2 Star</option>
            <option value="3 Star">3 Star</option>
            <option value="4 Star">4 Star</option>
            <option value="5 Star">5 Star</option>
            <option value="Luxury">Luxury</option>
            <option value="Villa">Villa</option>
            <option value="Home Stay">Home Stay</option>
          </select>
        </Col>

        <Col md={3}>
          <select
            className="form-select"
            value={location}
            onChange={(e) => {
              setLocation(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All Locations</option>

            {places.map((p) => (
              <option key={p._id} value={p._id}>
                {p.name}
              </option>
            ))}
          </select>
        </Col>

        <Col md={3}>
          <button
            type="button"
            className="cancel-btn w-75"
            onClick={handleReset}
          >
            Reset Filters
          </button>
        </Col>
      </Row>

      {/* =========================
          HOTEL LIST
      ========================= */}
      <Container>
        <div className="roomtype-grid mb-5">

          {loading ? (
            <div className="text-center py-5 text-muted">
              Loading Hotels...
            </div>
          ) : currentHotels.length === 0 ? (
            <div className="text-center py-5 text-muted">
              No Hotels Found
            </div>
          ) : (
            <div className="row g-4">
              {currentHotels.map((item) => (
                <div
                  className="col-lg-3 col-md-4 col-sm-6"
                  key={item._id}
                >
                  <Card className="hotel-select-card h-100">
                    <Card.Body className="d-flex flex-column justify-content-between">

                      <div className="hotel-icon-img">
                        <img
                          src={item.image}
                          alt={item.name}
                        />
                      </div>

                      <h6 className="fw-semibold mt-3 mb-1">
                        {item.name}
                      </h6>

                      <p className="text-muted small mb-3">
                        Select hotel to manage room types
                      </p>

                      <button
                        type="button"
                        className="cancel-btn w-100"
                        onClick={() =>
                          navigate(
                            `/admin/hotel-rooms/${item._id}`
                          )
                        }
                      >
                        View Rooms →
                      </button>

                    </Card.Body>
                  </Card>
                </div>
              ))}
            </div>
          )}

          {/* =========================
              PAGINATION
          ========================= */}
          {!loading && totalPages > 1 && (
            <div className="d-flex justify-content-center align-items-center gap-2 mt-5 flex-wrap">

              {/* PREVIOUS */}
              <Button
                variant="light"
                disabled={currentPage === 1}
                onClick={() =>
                  handlePageChange(currentPage - 1)
                }
              >
                ‹
              </Button>

              {/* PAGE NUMBERS */}
              {paginationPages.map((page, index) => {
                const previousPage =
                  paginationPages[index - 1];

                return (
                  <div
                    key={page}
                    className="d-flex align-items-center gap-2"
                  >
                    {/* DOTS */}
                    {previousPage &&
                      page - previousPage > 1 && (
                        <span>...</span>
                      )}

                    <Button
                      variant={
                        currentPage === page
                          ? "dark"
                          : "light"
                      }
                      onClick={() =>
                        handlePageChange(page)
                      }
                    >
                      {page}
                    </Button>
                  </div>
                );
              })}

              {/* NEXT */}
              <Button
                variant="light"
                disabled={currentPage === totalPages}
                onClick={() =>
                  handlePageChange(currentPage + 1)
                }
              >
                ›
              </Button>

            </div>
          )}

        </div>
      </Container>
    </>
  );
}